import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Colours from '@shared/ui/atoms/Colours';
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { loadKioskVisitors, selectKiosk, unpairThisKiosk } from '../data/kiosk.duck';

function formatToday(date: Date): string {
  return date.toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long',
  });
}

export function KioskHeader() {
  const dispatch = useDispatch();
  const kiosk = useSelector(selectKiosk);
  const [showUnpair, setShowUnpair] = useState(false);
  const [today, setToday] = useState(() => formatToday(new Date()));

  useEffect(() => {
    const tick = () => setToday(formatToday(new Date()));
    const id = window.setInterval(tick, 60_000);
    return () => window.clearInterval(id);
  }, []);

  const handleUnpair = () => {
    if (window.confirm('Unpair this kiosk?')) {
      dispatch(unpairThisKiosk());
    }
    setShowUnpair(false);
  };

  const handleRefresh = () => {
    dispatch(loadKioskVisitors());
  };

  return (
    <Wrapper>
      <Left>
        <BrandLogo src="/assets/images/logos/team-today-logo.svg" alt="Team Today" />
        <CompanyInfo>
          <BodyLarge weight={600}>{kiosk.companyName || 'Visitor check-in'}</BodyLarge>
          <BodyRegular style={{ color: Colours.darkGrey }}>
            {kiosk.officeLabel ? `${kiosk.officeLabel} · ` : ''}{today}
          </BodyRegular>
        </CompanyInfo>
      </Left>
      <Right>
        <IconButton
          type="button"
          onClick={handleRefresh}
          disabled={kiosk.loading}
          aria-label="Refresh"
          title="Refresh"
        >
          <RefreshIcon $spinning={kiosk.loading}>↻</RefreshIcon>
        </IconButton>
        <IconButton type="button" onClick={() => setShowUnpair(v => !v)} aria-label="Settings" title="Settings">⚙</IconButton>
        {showUnpair && (
          <UnpairMenu>
            <BodySmall>Unpair this tablet?</BodySmall>
            <UnpairButton type="button" onClick={handleUnpair}>Unpair</UnpairButton>
            <UnpairButton type="button" $variant="ghost" onClick={() => setShowUnpair(false)}>Cancel</UnpairButton>
          </UnpairMenu>
        )}
      </Right>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
  background: white;
  border-bottom: 1px solid ${Colours.lightGrey};
  position: relative;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BrandLogo = styled.img`
  height: 36px;
  width: auto;
`;

const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 16px;
  border-left: 1px solid ${Colours.lightGrey};
`;

const Right = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconButton = styled.button`
  width: 44px;
  height: 44px;
  font-size: 22px;
  border: 1px solid ${Colours.mildGrey};
  border-radius: 8px;
  background: white;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  &:disabled { opacity: 0.5; cursor: default; }
`;

const RefreshIcon = styled.span<{ $spinning: boolean }>`
  display: inline-block;
  line-height: 1;
  animation: ${p => p.$spinning ? 'kiosk-refresh-spin 0.8s linear infinite' : 'none'};
  @keyframes kiosk-refresh-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const UnpairMenu = styled.div`
  position: absolute;
  right: 0;
  top: 52px;
  background: white;
  border: 1px solid ${Colours.lightGrey};
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 220px;
  z-index: 10;
  box-shadow: 0 8px 24px rgba(0,0,0,0.08);
`;

const UnpairButton = styled.button<{ $variant?: 'ghost' }>`
  height: 40px;
  border-radius: 6px;
  border: 1px solid ${p => p.$variant === 'ghost' ? Colours.mildGrey : Colours.red};
  background: ${p => p.$variant === 'ghost' ? 'white' : Colours.red};
  color: ${p => p.$variant === 'ghost' ? Colours.darkGrey : 'white'};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
`;
